import type { LeadershipMetrics } from '@delivery-metrics/shared';
import {
  AccountBalance,
  Assessment,
  Speed,
  Timeline,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';
import React from 'react';

interface ROIAnalysisSectionProps {
  data: LeadershipMetrics | null;
  totalFeatures: number;
  totalEffort: number;
}

export const ROIAnalysisSection: React.FC<ROIAnalysisSectionProps> = ({
  data,
  totalFeatures,
  // totalEffort kept for consistency but not used
}) => {
  // Get time efficiency metrics from data
  const timeEfficiency = data?.kpis?.timeEfficiency;

  // Convert effort from hours to FTE months (assuming 160 hours per month)
  const effortInMonths = timeEfficiency
    ? timeEfficiency.effortInvestment / 160
    : 0;

  // Use total features as value delivered
  const featuresDelivered = totalFeatures;

  // Calculate time efficiency as features per FTE month
  const timeEfficiencyValue =
    effortInMonths > 0 ? featuresDelivered / effortInMonths : 0;

  if (!timeEfficiency) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h5'
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
        >
          <AccountBalance sx={{ mr: 1 }} />
          Time Efficiency Analysis
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3].map(i => (
            <Grid size={{ xs: 12, md: 4 }} key={i}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Speed color='primary' />
                    <Typography variant='h6' sx={{ ml: 1 }}>
                      Loading...
                    </Typography>
                  </Box>
                  <Typography variant='h4' color='primary.main'>
                    --
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    Loading data...
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant='h5'
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
      >
        <AccountBalance sx={{ mr: 1 }} />
        Time Efficiency Analysis
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Timeline color='primary' />
                <Typography variant='h6' sx={{ ml: 1 }}>
                  Effort Investment
                </Typography>
              </Box>
              <Typography variant='h4' color='primary.main'>
                {effortInMonths.toFixed(1)} FTE months
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Total effort invested
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment color='success' />
                <Typography variant='h6' sx={{ ml: 1 }}>
                  Value Delivered
                </Typography>
              </Box>
              <Typography variant='h4' color='success.main'>
                {featuresDelivered}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Features delivered
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Speed color='info' />
                <Typography variant='h6' sx={{ ml: 1 }}>
                  Time Efficiency
                </Typography>
              </Box>
              <Typography variant='h4' color='info.main'>
                {timeEfficiencyValue.toFixed(2)}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Features per FTE month
              </Typography>
              <LinearProgress
                variant='determinate'
                value={Math.min((timeEfficiencyValue / 5) * 100, 100)} // Scale: 5 features/month = 100%
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
